/**
 * アクティビティカード
 *
 * ランニング、ウォーキング等のアクティビティを表示
 */

import type { GarminActivitySummary } from '@/api/types/garmin'
import { formatDistanceMeters, formatTimeJST } from '@/lib/activity-format-utils'
import { formatDateShort } from '@/lib/format-utils'
import { getGarminActivityStyle } from '@/lib/garmin-activity-config'
import { formatSecondsJa } from '@/lib/time-format'
import { cn } from '@/lib/utils'

type Props = {
  activity: GarminActivitySummary
  className?: string
}

export const ActivityCard = ({ activity, className }: Props) => {
  const distance = activity.distance_meters != null ? formatDistanceMeters(activity.distance_meters) : null
  const style = getGarminActivityStyle(activity.activity_type)
  const Icon = style.icon

  return (
    <div
      className={cn(
        'rounded-xl border border-l-4 bg-card p-4 shadow-sm transition-all hover:shadow-md',
        style.borderColor,
        className,
      )}
    >
      <div className="flex items-start gap-3">
        {/* アイコン */}
        <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
          <Icon className={cn('size-5', style.color)} />
        </div>

        {/* コンテンツ */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium truncate">{activity.activity_name}</h3>
              <p className="text-xs text-muted-foreground">
                {formatDateShort(activity.start_time)} {formatTimeJST(activity.start_time)}
              </p>
            </div>
          </div>

          {/* メトリクス */}
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">時間</span>
              <span className="font-medium tabular-nums">{formatSecondsJa(activity.duration_seconds)}</span>
            </div>
            {distance && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">距離</span>
                <span className="font-medium tabular-nums">{distance}</span>
              </div>
            )}
            {activity.calories && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">カロリー</span>
                <span className="font-medium tabular-nums">{activity.calories}</span>
              </div>
            )}
            {activity.avg_hr && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">平均HR</span>
                <span className="font-medium tabular-nums">{activity.avg_hr}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
